import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Upload,
  AlertTriangle,
  Calendar,
  Scale,
  FileUp,
  X,
  Loader2,
  CheckCircle,
  Clock,
  Gavel,
  Home,
  ClipboardList,
  FileSearch,
  CalendarClock,
  ListTodo,
  FolderOpen,
  PenTool,
  Building,
  Hash,
  User,
  Download,
  Copy
} from "lucide-react";
import { Button } from "../components/ui/button";
import ThemeToggle from "../components/ThemeToggle";
import { ScrollArea } from "../components/ui/scroll-area";
import { NoticeAnalysisSkeleton } from "../components/skeletons/NoticeAnalysisSkeleton";
import { API_BASE_URL, API_URL } from "../utils/api";

const BACKEND_URL = API_BASE_URL;
const API = API_URL;

// Mobile App API for Single Source of Truth
const MOBILE_API = process.env.REACT_APP_MOBILE_API || API_URL;

const NoticePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [uploadedFile, setUploadedFile] = useState(null);
  const [noticeText, setNoticeText] = useState("");
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [extractedInfo, setExtractedInfo] = useState(null);

  const tabs = [
    { id: "summary", label: "Summary", icon: ClipboardList },
    { id: "analysis", label: "Analysis", icon: FileSearch },
    { id: "timeline", label: "Timeline", icon: CalendarClock },
    { id: "action", label: "Action", icon: ListTodo },
    { id: "docs", label: "Docs", icon: FolderOpen },
    { id: "draft", label: "Draft", icon: PenTool }
  ];

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error("Please upload a PDF file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size should be less than 10MB");
        return;
      }
      setUploadedFile(file);
      toast.success(`Notice PDF "${file.name}" attached`);
      
      // Extract text from PDF using backend
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('extract_only', 'true');
        
        const response = await fetch(`${API}/extract-pdf`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.text) {
            setNoticeText(data.text);
            toast.success("Text extracted from PDF");
          }
        }
      } catch (error) {
        console.log("PDF extraction optional - will send file to analysis");
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setNoticeText("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const analyzeNotice = async () => {
    if (!noticeText.trim() && !uploadedFile) {
      toast.error("Please upload a notice PDF or enter notice text");
      return;
    }

    setIsLoading(true);
    setAnalysisResult(null);
    setExtractedInfo(null);

    try {
      const formData = new FormData();
      
      if (uploadedFile) {
        formData.append('file', uploadedFile);
      }
      formData.append('notice_text', noticeText);
      formData.append('session_id', `notice-${Date.now()}`);
      
      // Use regular endpoint with longer timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minute timeout
      
      const response = await fetch(`${API}/analyze-notice`, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        let errorMsg = 'Analysis request failed';
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch (e) {
          errorMsg = `Server error (${response.status})`;
        }
        throw new Error(errorMsg);
      }
      
      const result = await response.json();
      
      if (result.structured && result.data) {
        // Structured JSON response
        setAnalysisResult({
          summary: result.data.summary || {},
          analysis: result.data.analysis || {},
          timeline: result.data.timeline || {},
          action_items: result.data.action_items || [],
          documents_required: result.data.documents_required || [],
          draft_reply: result.data.draft_reply || "",
          case_laws: result.data.case_laws || []
        });
        
        // Set extracted info from structured response
        if (result.data.summary) {
          setExtractedInfo({
            section: result.data.summary.section || "",
            assessmentYear: result.data.summary.assessment_year || "",
            noticeDate: result.data.summary.notice_date || "",
            dinNumber: result.data.summary.din_number || "",
            authority: result.data.summary.issuing_authority || "",
            inSimpleTerms: result.data.summary.in_simple_terms || ""
          });
        }
      } else if (result.analysis) {
        // Plain text response - parse it
        const analysisText = result.analysis || "";
        setAnalysisResult({
          summary: {},
          analysis: analysisText,
          draft_reply: extractDraftFromText(analysisText),
          action_items: extractBulletPoints(analysisText),
          documents_required: [],
          timeline: {}
        });
        
        // Extract info from text
        extractKeyInfo(noticeText || "");
      } else {
        console.error("Unexpected response format:", result);
        throw new Error("Invalid response format from server");
      }
      
      toast.success("Notice analyzed successfully!");

    } catch (error) {
      console.error("Analysis error:", error);
      console.error("Error details:", error.message, error.stack);
      // Show error for longer duration (10 seconds)
      toast.error(error.message || "Failed to analyze notice. Please try again.", {
        duration: 10000,
        description: "Please try again or contact support if the issue persists."
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const extractDraftFromText = (text) => {
    // Look for draft section in the text
    const draftMatch = text.match(/draft[^:]*:([\s\S]*?)(?=\n#|\n\*\*[A-Z]|$)/i);
    return draftMatch ? draftMatch[1].trim() : "";
  };

  const extractKeyInfo = (text) => {
    // Extract common patterns from notice text
    const info = {
      section: text.match(/section\s*(\d+[A-Za-z]*(?:\(\d+\))?)/i)?.[1] || "",
      assessmentYear: text.match(/(?:AY|Assessment Year)\s*(\d{4}-\d{2,4})/i)?.[1] || "",
      noticeDate: text.match(/dated?\s*[:\s]*(\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}|\d{1,2}\s+\w+\s+\d{4})/i)?.[1] || "",
      dinNumber: text.match(/DIN[:\s]*([A-Z0-9]+)/i)?.[1] || "",
      authority: text.match(/(?:Deputy|Assistant|Joint|Additional)?\s*(?:Commissioner|Director)\s*(?:of\s*)?(?:Income\s*Tax)?[^,\n]*/i)?.[0] || ""
    };
    setExtractedInfo(info);
  };

  const parseAnalysisIntoSections = (analysisText) => {
    // Parse the analysis text into structured sections
    const result = {
      analysis: analysisText,
      draft_reply: extractSection(analysisText, "draft", "reply", "response"),
      key_points: extractBulletPoints(analysisText),
      deadline_info: extractSection(analysisText, "deadline", "timeline", "date"),
      relevant_sections: extractSection(analysisText, "section", "provision", "legal")
    };
    setAnalysisResult(result);
  };

  const extractSection = (text, ...keywords) => {
    for (const keyword of keywords) {
      const regex = new RegExp(`(?:^|\\n).*${keyword}.*(?:\\n(?!#|\\*\\*)[^\\n]+)+`, 'gi');
      const match = text.match(regex);
      if (match) return match[0];
    }
    return "";
  };

  const extractBulletPoints = (text) => {
    const bullets = text.match(/[-•]\s*[^\n]+/g) || [];
    return bullets.slice(0, 10);
  };

  const formatContent = (content) => {
    if (!content) return "";
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="text-slate-100 italic">$1</em>')
      .replace(/```([^`]+)```/g, '<pre class="bg-slate-900 p-3 rounded-lg text-sm my-2 overflow-x-auto">$1</pre>')
      .split('\n')
      .map(line => {
        if (line.startsWith('### ') || line.startsWith('# ')) {
          return `<h3 class="text-white font-semibold mt-4 mb-2 text-lg">${line.replace(/^#+ /, '')}</h3>`;
        }
        if (line.startsWith('## ')) {
          return `<h2 class="text-white font-bold text-xl mt-5 mb-2">${line.slice(3)}</h2>`;
        }
        if (line.startsWith('- ') || line.startsWith('• ')) {
          return `<li class="ml-4 text-slate-200 my-1">${line.slice(2)}</li>`;
        }
        if (/^\d+\.\s/.test(line)) {
          return `<li class="ml-4 text-slate-200 my-1">${line}</li>`;
        }
        if (line.includes('|') && !line.includes('http')) {
          if (line.match(/^\|[-\s|]+\|$/)) {
            return '';
          }
          return `<p class="my-0 font-mono text-sm text-slate-300">${line}</p>`;
        }
        return line ? `<p class="my-1 text-slate-200">${line}</p>` : '<br/>';
      })
      .join('');
  };

  const renderTabContent = () => {
    if (!analysisResult) return null;

    switch (activeTab) {
      case "summary":
        return (
          <div className="space-y-4">
            {/* Executive Summary Card */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl p-5 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-cyan-400" />
                Executive Summary
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <Scale className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Notice Section</p>
                    <p className="text-white font-medium">{extractedInfo?.section || analysisResult?.summary?.section || "Not identified"}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Assessment Year</p>
                    <p className="text-white font-medium">{extractedInfo?.assessmentYear || analysisResult?.summary?.assessment_year || "Not specified"}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Notice Date</p>
                    <p className="text-white font-medium">{extractedInfo?.noticeDate || analysisResult?.summary?.notice_date || "Not specified"}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Hash className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Reference No.</p>
                    <p className="text-white font-medium text-sm truncate">{extractedInfo?.dinNumber || analysisResult?.summary?.din_number || "Not specified"}</p>
                  </div>
                </div>
              </div>
              
              {(extractedInfo?.authority || analysisResult?.summary?.issuing_authority) && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Building className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Issuing Authority</p>
                      <p className="text-white font-medium">{extractedInfo?.authority || analysisResult?.summary?.issuing_authority}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* In Simple Terms */}
            <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                💡 In Simple Terms
              </h3>
              <p className="text-slate-300 leading-relaxed">
                {extractedInfo?.inSimpleTerms || analysisResult?.summary?.in_simple_terms || 
                  (typeof analysisResult?.analysis === 'string' 
                    ? analysisResult.analysis.slice(0, 500) 
                    : analysisResult?.analysis?.legal_basis || "Analysis will appear here...")}
              </p>
            </div>
          </div>
        );

      case "analysis":
        const analysisData = analysisResult?.analysis;
        const analysisContent = typeof analysisData === 'object' 
          ? `## ⚖️ Jurisdictional Validity\n${analysisData.jurisdictional_validity || 'Not analyzed'}\n\n## 📜 Legal Basis\n${analysisData.legal_basis || 'Not specified'}\n\n## 🔍 Department Concerns\n${analysisData.department_concerns || 'Not specified'}\n\n## ⚠️ Potential Implications\n${analysisData.potential_implications || 'Not specified'}\n\n## ✅ Strengths (Your Position)\n${analysisData.strengths || 'Not specified'}\n\n## ❌ Weaknesses (Areas of Concern)\n${analysisData.weaknesses || 'Not specified'}`
          : (analysisData || "No analysis available");
        
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-cyan-400" />
              Detailed Analysis
            </h3>
            <div 
              className="text-slate-300 leading-relaxed prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: formatContent(analysisContent) }}
            />
          </div>
        );

      case "timeline":
        const timelineData = analysisResult?.timeline;
        const hasTimeline = timelineData && typeof timelineData === 'object' && Object.keys(timelineData).length > 0;
        
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-amber-400" />
              Timeline & Deadlines
            </h3>
            {hasTimeline ? (
              <div className="space-y-3">
                {timelineData.notice_date && (
                  <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-xl">
                    <Calendar className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Notice Date</p>
                      <p className="text-sm text-slate-400">{timelineData.notice_date}</p>
                    </div>
                  </div>
                )}
                {timelineData.response_deadline && (
                  <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-xl border border-red-500/30">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Response Deadline</p>
                      <p className="text-sm text-red-300 font-semibold">{timelineData.response_deadline}</p>
                    </div>
                  </div>
                )}
                {timelineData.hearing_date && (
                  <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
                    <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Hearing Date</p>
                      <p className="text-sm text-amber-300">{timelineData.hearing_date}</p>
                    </div>
                  </div>
                )}
                {timelineData.limitation_period && (
                  <div className="flex items-center gap-3 p-3 bg-purple-500/10 rounded-xl border border-purple-500/30">
                    <Scale className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">Limitation Period</p>
                      <p className="text-sm text-purple-300">{timelineData.limitation_period}</p>
                    </div>
                  </div>
                )}
                {timelineData.critical_action && (
                  <div className="flex items-start gap-3 p-3 bg-cyan-500/10 rounded-xl border border-cyan-500/30">
                    <Gavel className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Critical Action Required</p>
                      <p className="text-sm text-cyan-300">{timelineData.critical_action}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-red-500/10 rounded-xl border border-red-500/30">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-white font-medium">Response Deadline</p>
                    <p className="text-sm text-slate-400">Typically 15-30 days from notice date</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
                  <Clock className="w-5 h-5 text-amber-400" />
                  <div>
                    <p className="text-white font-medium">Hearing Date</p>
                    <p className="text-sm text-slate-400">As mentioned in the notice</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "action":
        // Use action_items from API response or fallback to key_points
        const actionItems = analysisResult?.action_items || analysisResult?.key_points || [];
        const hasActionItems = Array.isArray(actionItems) && actionItems.length > 0;
        
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-green-400" />
              Action Items
            </h3>
            {hasActionItems ? (
              <ul className="space-y-2">
                {actionItems.map((item, idx) => {
                  // Handle both string and object formats
                  const itemText = typeof item === 'string' ? item : item?.text || item?.action || JSON.stringify(item);
                  return (
                    <li key={idx} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{itemText.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '')}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <ul className="space-y-2">
                <li className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Review the notice carefully and note all dates</span>
                </li>
                <li className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Gather all relevant documents mentioned</span>
                </li>
                <li className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Prepare point-wise response to each query</span>
                </li>
                <li className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-slate-300">Consult a CA/Tax Professional if needed</span>
                </li>
              </ul>
            )}
          </div>
        );

      case "docs":
        const docsRequired = analysisResult?.documents_required || [];
        const hasDocsData = Array.isArray(docsRequired) && docsRequired.length > 0;
        
        const getPriorityColor = (priority) => {
          const p = (priority || 'medium').toLowerCase();
          if (p === 'high') return 'bg-red-500/20 text-red-400';
          if (p === 'low') return 'bg-green-500/20 text-green-400';
          return 'bg-amber-500/20 text-amber-400';
        };
        
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-purple-400" />
              Documents to Gather
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 px-3 text-slate-400 text-sm font-medium">Document</th>
                    <th className="text-left py-2 px-3 text-slate-400 text-sm font-medium">Purpose</th>
                    <th className="text-center py-2 px-3 text-slate-400 text-sm font-medium">Priority</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {hasDocsData ? (
                    docsRequired.map((doc, idx) => (
                      <tr key={idx} className="border-b border-slate-700/50">
                        <td className="py-3 px-3 text-white">{doc.document || doc.name || 'Document'}</td>
                        <td className="py-3 px-3 text-slate-400">{doc.purpose || 'As required'}</td>
                        <td className="py-3 px-3 text-center">
                          <span className={`px-2 py-1 ${getPriorityColor(doc.priority)} rounded-full text-xs`}>
                            {(doc.priority || 'Medium').charAt(0).toUpperCase() + (doc.priority || 'Medium').slice(1).toLowerCase()}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <>
                      <tr className="border-b border-slate-700/50">
                        <td className="py-3 px-3 text-white">ITR Copy</td>
                        <td className="py-3 px-3 text-slate-400">Filed return for the AY</td>
                        <td className="py-3 px-3 text-center"><span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">High</span></td>
                      </tr>
                      <tr className="border-b border-slate-700/50">
                        <td className="py-3 px-3 text-white">Form 16/16A</td>
                        <td className="py-3 px-3 text-slate-400">TDS certificates</td>
                        <td className="py-3 px-3 text-center"><span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">High</span></td>
                      </tr>
                      <tr className="border-b border-slate-700/50">
                        <td className="py-3 px-3 text-white">Bank Statements</td>
                        <td className="py-3 px-3 text-slate-400">For the relevant FY</td>
                        <td className="py-3 px-3 text-center"><span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs">Medium</span></td>
                      </tr>
                      <tr className="border-b border-slate-700/50">
                        <td className="py-3 px-3 text-white">Investment Proofs</td>
                        <td className="py-3 px-3 text-slate-400">80C, 80D deductions</td>
                        <td className="py-3 px-3 text-center"><span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs">Medium</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 px-3 text-white">26AS Statement</td>
                        <td className="py-3 px-3 text-slate-400">Tax credit statement</td>
                        <td className="py-3 px-3 text-center"><span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">High</span></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case "draft":
        const draftText = analysisResult.draft_reply?.replace(/```/g, '') || "";
        
        const downloadAsWord = () => {
          const header = `
            <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
            <head><meta charset='utf-8'><title>Notice Response Draft</title>
            <style>
              body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.6; margin: 1in; }
              pre { font-family: 'Times New Roman', serif; white-space: pre-wrap; }
            </style>
            </head><body>
          `;
          const footer = "</body></html>";
          const content = header + "<pre>" + draftText + "</pre>" + footer;
          
          const blob = new Blob([content], { type: 'application/msword' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Notice_Response_${extractedInfo?.section || 'Draft'}_${Date.now()}.doc`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          toast.success("Draft downloaded as Word document!");
        };
        
        return (
          <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <PenTool className="w-5 h-5 text-cyan-400" />
              Draft Response
            </h3>
            {draftText ? (
              <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm max-h-[400px] overflow-y-auto">
                <pre className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                  {draftText}
                </pre>
              </div>
            ) : (
              <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm">
                <pre className="text-slate-300 whitespace-pre-wrap leading-relaxed">
{`To,
The Assessing Officer,
Income Tax Department,
[Jurisdiction]

Subject: Reply to Notice u/s ${extractedInfo?.section || "___"} for AY ${extractedInfo?.assessmentYear || "____-__"}

Ref: Notice No. ${extractedInfo?.dinNumber || "___________"}

Respected Sir/Madam,

With reference to the above-mentioned notice, I hereby submit my response as follows:

1. I acknowledge receipt of the notice dated ${extractedInfo?.noticeDate || "___________"}.

2. [Point-wise response to queries raised in the notice]

3. [Supporting documents enclosed as per annexure]

I request your good office to consider the above submissions and documents favorably.

Thanking you,

Yours faithfully,
[Name]
[PAN]
[Address]
[Date]

Encl: As above`}
                </pre>
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <Button 
                className="flex-1 bg-slate-700/50 text-white hover:bg-slate-600/50 border border-slate-600"
                onClick={() => {
                  const text = draftText || "Draft response template";
                  navigator.clipboard.writeText(text);
                  toast.success("Draft copied to clipboard!");
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Draft
              </Button>
              <Button 
                className="flex-1 bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
                onClick={downloadAsWord}
              >
                <Download className="w-4 h-4 mr-2" />
                Download as Word
              </Button>
            </div>
            
            {/* Case Laws Section - Separate from Draft */}
            {analysisResult?.case_laws && analysisResult.case_laws.length > 0 && (
              <div className="mt-6 bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
                <div className="flex items-start gap-2 mb-3">
                  <Scale className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-amber-400 font-semibold">⚖️ Supporting Case Laws (For Your Reference)</h4>
                    <p className="text-xs text-amber-300/70 mt-1">
                      ⚠️ DISCLAIMER: Please verify these case laws independently before including them in your reply. 
                      Cross-check citations from official sources like itatonline.org or indiankanoon.org.
                    </p>
                  </div>
                </div>
                <div className="space-y-3 mt-4">
                  {analysisResult.case_laws.map((caseItem, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{caseItem.case_name}</p>
                          <p className="text-cyan-400 text-xs mt-1 font-mono">{caseItem.citation}</p>
                          <p className="text-slate-300 text-sm mt-2 italic">"{caseItem.ruling_summary || caseItem.relevance}"</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                          caseItem.court === 'SC' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' :
                          caseItem.court === 'HC' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                          'bg-green-500/20 text-green-400 border border-green-500/30'
                        }`}>
                          {caseItem.court}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3 italic">
                  💡 Tip: If you want to include case laws in your reply, add them under a "LEGAL & JUDICIAL SUPPORT" section before the Prayer clause.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-30">
        <div className="w-full px-4 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-semibold text-white">Notice Analyzer</h1>
                <p className="text-xs text-slate-500">AI-Powered IT Notice Analysis</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => navigate('/chat')}
              className="text-slate-400 hover:text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              Chat
            </Button>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
      </header>

      <div className="w-full max-w-6xl mx-auto px-4 lg:px-8 py-6">
        {isLoading ? (
          /* Loading Skeleton */
          <NoticeAnalysisSkeleton />
        ) : !analysisResult ? (
          /* Upload Section */
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <FileUp className="w-5 h-5 text-cyan-400" />
                Upload Notice or Enter Text
              </h2>
              
              {/* File Upload */}
              <div className="border-2 border-dashed border-slate-700 rounded-xl p-6 text-center hover:border-cyan-500/50 transition-colors mb-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                {uploadedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-cyan-400" />
                    <div className="text-left">
                      <p className="text-white font-medium">{uploadedFile.name}</p>
                      <p className="text-sm text-slate-400">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={removeFile}
                      className="text-slate-400 hover:text-red-400"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="cursor-pointer"
                  >
                    <Upload className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-400">Click to upload PDF</p>
                    <p className="text-sm text-slate-500 mt-1">or drag and drop</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-slate-700"></div>
                <span className="text-slate-500 text-sm">OR</span>
                <div className="flex-1 h-px bg-slate-700"></div>
              </div>

              {/* Text Input */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Paste Notice Content</label>
                <textarea
                  value={noticeText}
                  onChange={(e) => setNoticeText(e.target.value)}
                  placeholder="Paste the complete notice text here for accurate analysis..."
                  rows={8}
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 resize-none"
                />
              </div>

              <Button
                onClick={analyzeNotice}
                disabled={isLoading || (!noticeText.trim() && !uploadedFile)}
                className="w-full mt-4 btn-primary text-white rounded-xl py-3"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing Notice...
                  </>
                ) : (
                  <>
                    <Gavel className="w-5 h-5 mr-2" />
                    Analyze Notice
                  </>
                )}
              </Button>
            </motion.div>
          </div>
        ) : (
          /* Results Section with Tabs */
          <div className="space-y-4">
            {/* Back Button */}
            <Button
              variant="ghost"
              onClick={() => {
                setAnalysisResult(null);
                setExtractedInfo(null);
                setNoticeText("");
                setUploadedFile(null);
              }}
              className="text-slate-400 hover:text-white mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Analyze Another Notice
            </Button>

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-slate-800/50 rounded-xl overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-cyan-500 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <ScrollArea className="h-[calc(100vh-280px)]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {renderTabContent()}
                </motion.div>
              </AnimatePresence>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
};

export default NoticePage;
